/* eslint-disable */
import React, { useState, useMemo, useEffect } from 'react';
import './staffAugmentationReport.scss';
import { StaffAugmentationReportProvider } from '../../../Contexts/StaffAugmentationReportContext';
import { useStaffAugmentationReport } from '../../../Hooks/useStaffAugmentationReport';
import {
  STAFF_AUG_REPORT,
} from '../../../Constants/strings';
import AccordionWrapper from '../../../Components/AccordionWrapper/AccordionWrapper';
import ReportFiltersBar from './CommonReportFilter/reportFiltersBar';

import OpportunitiesOverviewReport from './OpportunitiesOverview/opportunitiesOverviewReport';
import ClouserReport from './WeeklyClosureReport/clouserReport';
import LostDroppedReport from './LostDroppedReport/lostDroppedReport';
import PendingPositionReport from './PendingPositionsReport/pendingPositionsReport';
import SalesAndDeliveryWiseData from './SalesAndDeliveryWiseDataReport/salesAndDeliveryWiseData';
import ResourcesPendingOnboarding from './PendingOnboardingData/resourcesPendingOnboarding';

import { defaultDateRanges } from './defaultDate';
import { useOpportunitiesOverview } from './useOpportunitiesOverview';
import { useLostDroppedReport } from './useLostDroppedReport';
import { usePendingPositionsReport } from './usePendingPositionsReport';
import { useResourcePendingOnboarding } from './useResourcePendingOnboarding';
import dayjs from 'dayjs';

const UtilityBar = () => (
  <div className="staff-augmentation-dashboard-utility-bar">
    <h1 className="staff-augmentation-dashboard-title">
      {STAFF_AUG_REPORT.REPORT_TITLE}
    </h1>
  </div>
);

const StaffAugmentationReport = () => {
  const {
    accordionState,
    expandIndividual,
    expandAllHandler,
    allExpanded,
  } = useStaffAugmentationReport();

  const initialDateRange = defaultDateRanges.find(r => r.label === 'This Week') || {};
  const [dealType, setDealType] = useState('');
  const [downloadType, setDownloadType] = useState('');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: initialDateRange.startDate,
    endDate: initialDateRange.endDate,
  });
  const [dateRangeKey, setDateRangeKey] = useState(Date.now());

  const handleDealTypeChange = (newDealType) => {
    setDealType(newDealType);
  };

  // -------------------- Report Hooks --------------------
  const {
    opportunitiesRows,
    opportunitiesDateRange,
    setOpportunitiesDateRange,
    opportunitiesPageSize,
    getOpportunitiesOverviewData,
  } = useOpportunitiesOverview(initialDateRange);

  const {
    lostDroppedRows,
    lostDroppedData,
    lostDroppedPageSize,
    lostDroppedDateRange,
    setLostDroppedDateRange,
    getLostDroppedData,
  } = useLostDroppedReport(initialDateRange);

  const {
    pendingPositionsRows,
    pendingPositionsData,
    pendingPositionsPageSize,
    pendingPositionsDateRange,
    setPendingPositionsDateRange,
    getPendingPositionsData,
    updatePendingPositionsData,
    downloadPendingPositionsExcel,
  } = usePendingPositionsReport(initialDateRange);

  const {
    getPendingOnboardingData,
    rows: pendingOnboardingRows,
    getPendingOnboardingDataDownload,
    pendingOnboarding,
    dateRange,
    setDateRange,
  } = useResourcePendingOnboarding(initialDateRange);

  // -------------------- Handlers --------------------
  const formatDate = (date) =>
    date ? dayjs(date).format('YYYY-MM-DD') : '';

  const debounce = (func, delay) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), delay);
    };
  };
  
  const debouncedFetchData = debounce((startDate, endDate, dealType) => {
    getOpportunitiesOverviewData(startDate, endDate, dealType);
    getLostDroppedData(startDate, endDate, dealType);
    getPendingPositionsData(startDate, endDate, dealType);
    getPendingOnboardingData(startDate, endDate, dealType);
  }, 300);
  
  const fetchReportData = (dealTypeOverride = dealType) => {
    const { startDate, endDate } = customDateRange;
    if (!startDate || !endDate) return;
  
    const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
    const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');
  
    getOpportunitiesOverviewData(formattedStartDate, formattedEndDate, dealTypeOverride);
    getLostDroppedData(formattedStartDate, formattedEndDate, dealTypeOverride);
    getPendingPositionsData(formattedStartDate, formattedEndDate, dealTypeOverride);
    getPendingOnboardingData(formattedStartDate, formattedEndDate, dealTypeOverride);
  };

  const handleDateRangeChange = (newRange) => {
    setCustomDateRange(newRange);
    setOpportunitiesDateRange(newRange);
    setLostDroppedDateRange(newRange);
    setPendingPositionsDateRange(newRange);
    setDateRange(newRange);
  
    const formattedStartDate = newRange.startDate 
    ? dayjs(newRange.startDate).format('YYYY-MM-DD') 
    : '';
    const formattedEndDate = newRange.endDate 
    ? dayjs(newRange.endDate).format('YYYY-MM-DD') 
    : '';  
    if (formattedStartDate && formattedEndDate) {
      debouncedFetchData(formattedStartDate, formattedEndDate, dealType);
    }
  };

  useEffect(() => {
    const { startDate, endDate } = customDateRange;
    if (startDate && endDate) {
      const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
      const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');
      // getOpportunitiesOverviewData(formattedStartDate, formattedEndDate, dealType);
      // getLostDroppedData(formattedStartDate, formattedEndDate, dealType);
      // getPendingPositionsData(formattedStartDate, formattedEndDate, dealType);
      // getPendingOnboardingData(formattedStartDate, formattedEndDate, dealType);
      debouncedFetchData(formattedStartDate, formattedEndDate, dealType);
    }
  }, [customDateRange, dealType]); // Dependencies on customDateRange and dealType

  const handleClearFilters = () => {
    const defaultRange = defaultDateRanges.find((r) => r.label === 'This Week') || {};

    setCustomDateRange({ startDate: defaultRange.startDate, endDate: defaultRange.endDate });
    setOpportunitiesDateRange(defaultRange);
    setLostDroppedDateRange(defaultRange);
    setPendingPositionsDateRange(defaultRange);
    setDateRange(defaultRange);

    setDealType('');
    setDownloadType('');
    setDateRangeKey(Date.now());

    // const formattedStartDate = formatDate(defaultRange.startDate);
    // const formattedEndDate = formatDate(defaultRange.endDate);

    // if (formattedStartDate && formattedEndDate) {
    //   getOpportunitiesOverviewData(formattedStartDate, formattedEndDate);
    //   getLostDroppedData(formattedStartDate, formattedEndDate);
    //   getPendingPositionsData(formattedStartDate, formattedEndDate);
    //   getPendingOnboardingData(formattedStartDate, formattedEndDate);
    // }
  };

  // -------------------- Accordion Configuration --------------------
  const accordionConfiguration = useMemo(() => ({
    [STAFF_AUG_REPORT.OPP_OVERVIEW]: {
      title: STAFF_AUG_REPORT.OPP_OVERVIEW,
      state: accordionState[STAFF_AUG_REPORT.OPP_OVERVIEW],
      openHandler: () => expandIndividual(STAFF_AUG_REPORT.OPP_OVERVIEW),
      data: (
        <OpportunitiesOverviewReport
          opportunitiesRows={opportunitiesRows}
          initialDateRange={initialDateRange}
          opportunitiesDateRange={opportunitiesDateRange}
          setOpportunitiesDateRange={setOpportunitiesDateRange}
          opportunitiesPageSize={opportunitiesPageSize}
          getOpportunitiesOverviewData={(start, end) =>
            getOpportunitiesOverviewData(start, end, dealType)
          }
        />
      ),
    },
    [STAFF_AUG_REPORT.LOST_DROPPED]: {
      title: STAFF_AUG_REPORT.LOST_DROPPED,
      state: accordionState[STAFF_AUG_REPORT.LOST_DROPPED],
      openHandler: () => expandIndividual(STAFF_AUG_REPORT.LOST_DROPPED),
      data: (
        <LostDroppedReport
          rows={lostDroppedRows}
          lostDroppedData={lostDroppedData}
          pageSize={lostDroppedPageSize}
          dateRange={lostDroppedDateRange}
          setDateRange={setLostDroppedDateRange}
          onDateChange={(start, end) =>
            getLostDroppedData(start, end, dealType)
          }
        />
      ),
    },
    [STAFF_AUG_REPORT.PENDING_POSITIONS]: {
      title: STAFF_AUG_REPORT.PENDING_POSITIONS,
      state: accordionState[STAFF_AUG_REPORT.PENDING_POSITIONS],
      openHandler: () => expandIndividual(STAFF_AUG_REPORT.PENDING_POSITIONS),
      data: (
        <PendingPositionReport
          rows={pendingPositionsRows}
          pendingPositionsData={pendingPositionsData}
          pageSize={pendingPositionsPageSize}
          dateRange={pendingPositionsDateRange}
          setDateRange={setPendingPositionsDateRange}
          getPendingPositionsData={(startDate, endDate) =>
            getPendingPositionsData(startDate, endDate, dealType)
          }
          updatePendingPositionsData={updatePendingPositionsData}
          downloadPendingPositionsExcel={downloadPendingPositionsExcel}
        />
      ),
    },
    [STAFF_AUG_REPORT.CLOUSRES_THIS_WEEK]: {
      title: STAFF_AUG_REPORT.CLOUSRES_THIS_WEEK,
      state: accordionState[STAFF_AUG_REPORT.CLOUSRES_THIS_WEEK],
      openHandler: () => expandIndividual(STAFF_AUG_REPORT.CLOUSRES_THIS_WEEK),
      data: <ClouserReport dateRange={customDateRange} dealType={dealType} />,
    },
    [STAFF_AUG_REPORT.DELVRY_MNGR_WSE_DATA]: {
      title: STAFF_AUG_REPORT.DELVRY_MNGR_WSE_DATA,
      state: accordionState[STAFF_AUG_REPORT.DELVRY_MNGR_WSE_DATA],
      openHandler: () => expandIndividual(STAFF_AUG_REPORT.DELVRY_MNGR_WSE_DATA),
      data: (
        <SalesAndDeliveryWiseData
          reportType="delivery"
          dateRange={customDateRange}
          dealType={dealType}
        />
      ),
    },
    [STAFF_AUG_REPORT.SALSE_MEMBRS_WSE_DATA]: {
      title: STAFF_AUG_REPORT.SALSE_MEMBRS_WSE_DATA,
      state: accordionState[STAFF_AUG_REPORT.SALSE_MEMBRS_WSE_DATA],
      openHandler: () => expandIndividual(STAFF_AUG_REPORT.SALSE_MEMBRS_WSE_DATA),
      data: (
        <SalesAndDeliveryWiseData
          reportType="sale"
          dateRange={customDateRange}
          dealType={dealType}
        />
      ),
    },
    [STAFF_AUG_REPORT.RSOURSE_PNDG_ONBRDNG]: {
      title: `${STAFF_AUG_REPORT.RSOURSE_PNDG_ONBRDNG}: ${pendingOnboarding}`,
      state: accordionState[STAFF_AUG_REPORT.RSOURSE_PNDG_ONBRDNG],
      openHandler: () => expandIndividual(STAFF_AUG_REPORT.RSOURSE_PNDG_ONBRDNG),
      data: (
        <ResourcesPendingOnboarding
          onboardingData={pendingOnboardingRows}
          onDateChange={getPendingOnboardingData}
          onDownloadExcel={getPendingOnboardingDataDownload}
          dateRange={dateRange}
          setDateRange={setDateRange}
        />
      ),
    },
  }), [
    accordionState,
    expandIndividual,
    opportunitiesRows,
    lostDroppedRows,
    pendingPositionsRows,
    pendingOnboardingRows,
    pendingOnboarding,
    dealType,
    customDateRange,
  ]);

  // -------------------- Render --------------------
  return (
    <div className="staff-aug-dashboard-outer-layer">
      <div className="staff-augmentation-dashboard">
        <UtilityBar />
        <ReportFiltersBar
          dateRangeKey={dateRangeKey}
          dateRange={customDateRange}
          handleDateRangeChange={(dateValue) => handleDateRangeChange(dateValue)}
          dealType={dealType}
          setDealType={(dealTypeVal) => handleDealTypeChange(dealTypeVal)}
          downloadType={downloadType}
          setDownloadType={setDownloadType}
          handleClearFilters={() => handleClearFilters()}
          definedRanges={defaultDateRanges}
          allExpanded={allExpanded}
          expandAllHandler={expandAllHandler}
          fetchReportData={fetchReportData}
        />
        <AccordionWrapper
          backgroundColor="#F1F5F8"
          configuration={accordionConfiguration}
          titleClassname="staff-aug-report-table-titles"
          accordionClassname="staff-aug-report-default"
        />
      </div>
    </div>
  );
};

const MainStaffAugmentationReport = () => (
  <StaffAugmentationReportProvider>
    <StaffAugmentationReport />
  </StaffAugmentationReportProvider>
);

export default MainStaffAugmentationReport;
